import {
  Pagination,
  PaginationContent,
  PaginationItem
} from "@workspace/web-ui/components/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@workspace/web-ui/components/table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { RefillMedicationHistory } from "../types";
import { formatRefillDate } from "@workspace/lib/date";
import { Button } from "@workspace/web-ui/components/button";
import { RefillHistoryModal } from "./refill-history-modal";

interface MedicalHistoryData {
  refillHistory: RefillMedicationHistory[];
}

const ITEMS_PER_PAGE = 10; // Display 5 items per page

export const getRefillMedicationStatusConfig = (item: RefillMedicationHistory) => {
  const status = item.refill_status

  const statusMap = {
    "completed": { color: "bg-[#E6FAF2] text-[#00C781] hover:bg-[#E6FAF2]", text: "Active", status: "completed" },
    "approved": { color: "bg-[#FEF2FF] text-[#942BA9]", text: "Approved", status: "approved" },
    "due": { color: "bg-[#FEEBEB] text-[#F53535] hover:bg-[#FEEBEB]", text: "Out of supply", status: "due" },
    "pending": { color: "bg-[#FFF6E7] text-[#FFAB1F]", text: "Pending", status: "pending" },
  };

  return statusMap[status as keyof typeof statusMap];
};

export default function MedicationRefillHistory({ refillHistory }: MedicalHistoryData) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRefill, setSelectedRefill] = useState<RefillMedicationHistory | null>(null);
  const [showRefillHistory, setShowRefillHistory] = useState(false);

  // Calculate total pages
  const totalPages = Math.ceil(refillHistory.length / ITEMS_PER_PAGE);

  // Get data for the current page
  const paginatedData = refillHistory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  


  return (
    <div className="lg:max-w-3xl mx-auto  bg-white shadow-md rounded-lg">
      <h2 className="text-lg font-medium  text-[#1A202C]  p-5">
        Medication History <span className="text-[#202020CC]">({refillHistory.length})</span>
      </h2>

      {/* Table */}
      <Table className="w-full rounded-lg">
        <TableHeader className="">
          <TableRow className="border-y text-xs min-w-0">
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">SN</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Medication</TableHead>
            <TableHead className="text-left text-[#202020CC] px-5 whitespace-nowrap">Dosage</TableHead>
            <TableHead className="text-left text-[#202020CC] px-5 whitespace-nowrap">Duration</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Date</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((item, index) =>{
            const {color, text} = getRefillMedicationStatusConfig(item)
            return (
              <TableRow key={index} className={`border-0 p-7 text-[#2D2F35] text-sm`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.medication.name}</TableCell>
                <TableCell className="px-5">{item.medication.dosage}</TableCell>
                <TableCell className="px-5">{item.medication.duration}</TableCell>
                <TableCell>{formatRefillDate(item.request_date)}</TableCell>
                <TableCell>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${color}`}
                  >
                    {text}
                  </span>
                </TableCell>
                <TableCell>
                  <Button onClick={() => {
                    setSelectedRefill(item)
                    setShowRefillHistory(true)
                  }} 
                  variant={"outline"}>View</Button>
                </TableCell>
              </TableRow>
            )
          } )}
          {/* <Separator className="w-full" /> */}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center mt-4 p-5 text-gray-600 text-sm border-t">
        <div className="flex-1">
          <span className=" text-[#464B55] text-sm font-normal inline-flex gap-1 ">
            Showing <h6 className="text-[#1A202C] font-medium">{Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, refillHistory.length)}</h6> to{" "}
            <h6 className="text-[#1A202C] font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, refillHistory.length)}</h6>  of <h6 className="text-[#1A202C] font-medium">{refillHistory.length}</h6> Results
          </span>
        </div>
        <div>

          <Pagination>
            <PaginationContent>
              <PaginationItem className="border rounded-md p-1 cursor-pointer">
                <ChevronLeft onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  className={`${currentPage === 1 ? "opacity-50 pointer-events-none" : ""} `}
                />
              </PaginationItem>
              <PaginationItem className="border rounded-md p-1 cursor-pointer">
                <ChevronRight
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  className={`${currentPage === totalPages ? "opacity-50 pointer-events-none" : ""} `}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
      {selectedRefill && <RefillHistoryModal refillMedication={selectedRefill} onClose={() => setShowRefillHistory(false)} open={showRefillHistory} />}
    </div>
  );
}
